import React, { useState, useEffect } from "react";
import { Modal, message } from "antd";
import { renderGrid } from "../common/FormRenderer";
import {
  PP_PICKLIST_LABELS,
  buildCreateFieldsForProjectPage,
} from "./projectPageColumnsConfig";
import { validateProjectPageEditForm } from "../../config/projectPageEdit.validation";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_PAGE } from "../../config/sobjectNames";

export default function CreateProjectPageModal({
  visible,
  onClose,
  onSuccess,
  initialValues = {},
}) {
  const { picklistMap } = usePicklistMap(
    Object.values(PP_PICKLIST_LABELS),
    SOBJECT_PROJECT_PAGE,
  );

  const [form, setForm] = useState({
    projectPageName: "",
    projectPageType: "",
    active: true,
    projectPageDescription: "",
    qualificationStep: "",
    project: "",
    projectId: "",
    projectObjective: "",
    projectObjectiveId: "",
  });
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isInitialEmpty, setIsInitialEmpty] = useState(false);

  // Apply incoming initial values when opening
  useEffect(() => {
    if (Object.keys(initialValues).length === 0) {
      setIsInitialEmpty(true);
    } else {
      setIsInitialEmpty(false);
    }
    if (initialValues !== undefined) {
      const {
        projectId,
        projectName,
        projectObjectiveId,
        projectObjectiveName,
      } = initialValues;
      if (projectId) {
        if (projectObjectiveId) {
          initialValues = {
            project: projectName,
            projectId: projectId,
            projectObjective: projectObjectiveName,
            projectObjectiveId: projectObjectiveId,
            ...initialValues,
          };
        } else {
          initialValues = {
            project: projectName,
            projectId: projectId,
            ...initialValues,
          };
        }
      }
    }
    if (visible && initialValues && typeof initialValues === "object") {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialValues]);

  const handleSave = async () => {
    // Validate form before saving
    const validationPayload = {
      info: {
        type: form.projectPageType,
        projectId: form.projectId || form.project,
      },
    };

    const errors = validateProjectPageEditForm(validationPayload);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    setSaving(true);
    try {
      // If both Project and Project Objective are selected, ensure they belong together
      if (
        (form.projectId || form.project) &&
        (form.projectObjectiveId || form.projectObjective)
      ) {
        try {
          const { fetchProjectObjective } =
            await import("../../services/projectObjectiveService");
          const po = await fetchProjectObjective(
            form.projectObjectiveId || form.projectObjective,
          );
          const poProjectId = po?.info?.projectId || po?.projectId || "";
          const selectedProjectId = String(
            form.projectId || form.project || "",
          ).trim();
          if (poProjectId && String(poProjectId) !== selectedProjectId) {
            message.error(
              "Selected Project Objective does not belong to the selected Project. Please choose a matching objective.",
            );
            setSaving(false);
            return;
          }
        } catch (prefetchErr) {
          // If we cannot verify, proceed but log for debugging
          // eslint-disable-next-line no-console
          console.warn(
            "Could not verify Project Objective project ownership:",
            prefetchErr,
          );
        }
      }

      // Enforce uniqueness of Project Page Type per Project and per Objective (if provided)
      try {
        const {
          fetchProjectPagesByProjectId,
          fetchProjectPagesByProjectObjectiveId,
        } = await import("../../services/projectPageService");
        const pageType = String(form.projectPageType || "").trim();
        const selectedObjectiveId = String(
          form.projectObjectiveId || form.projectObjective || "",
        ).trim();
        if (!selectedObjectiveId) {
          const selectedProjectId = String(
            form.projectId || form.project || "",
          ).trim();
          if (pageType && selectedProjectId) {
            // Check for existing page of same type under the project
            const projectPages =
              await fetchProjectPagesByProjectId(selectedProjectId);
            const hasSameTypeInProject = (
              Array.isArray(projectPages) ? projectPages : []
            ).some(
              (p) => String(p?.Project_Page_Type__c || "").trim() === pageType,
            );
            if (hasSameTypeInProject) {
              message.error(
                "A Project Page of this type already exists for the selected Project. Please choose a different type.",
              );
              setSaving(false);
              return;
            }
          }
        }

        // If an objective is selected, also ensure uniqueness at the objective level

        if (selectedObjectiveId) {
          const objectivePages =
            await fetchProjectPagesByProjectObjectiveId(selectedObjectiveId);
          const hasSameTypeInObjective = (
            Array.isArray(objectivePages) ? objectivePages : []
          ).some(
            (p) => String(p?.Project_Page_Type__c || "").trim() === pageType,
          );
          if (hasSameTypeInObjective) {
            message.error(
              "A Project Page of this type already exists for the selected Project Objective. Please choose a different type.",
            );
            setSaving(false);
            return;
          }
        }
      } catch (dupCheckErr) {
        // If we cannot verify uniqueness (e.g., network issue), proceed but log for debugging
        // eslint-disable-next-line no-console
        console.warn(
          "Could not verify Project Page type uniqueness:",
          dupCheckErr,
        );
      }

      const { createIndividualProjectPage } =
        await import("../../services/projectPageService");

      const result = await createIndividualProjectPage(form);
      if (result) {
        message.success("Project Page created successfully!");
        setForm({
          projectPageName: "",
          projectPageType: "",
          active: true,
          projectPageDescription: "",
          project: "",
          projectObjective: "",
        });
        onSuccess();
      } else {
        message.error(
          result?.error?.message || "Failed to create Project Page",
        );
      }
    } catch (error) {
      console.error("Error creating project page:", error);
      message.error(
        error.message || "An error occurred while creating the Project Page",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      projectPageName: "",
      projectPageType: "",
      active: true,
      projectPageDescription: "",
      project: "",
      projectObjective: "",
    });
    setValidationErrors({});
    onClose();
  };

  const handleChange = (path, value, displayName) => {
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
    setForm((prev) => {
      const update = { ...prev, [path]: value };
      if (displayName !== undefined) {
        update[`${path}DisplayName`] = displayName;
      }
      return update;
    });
  };
  const fields = resolvePicklistFields(
    buildCreateFieldsForProjectPage(form),
    picklistMap,
  );

  // Get validation error messages directly from centralized validation
  const errorMessages = Object.values(validationErrors);

  return (
    <Modal
      title="Create Project Page"
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={saving}
      width={700}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: 0 }}
    >
      {/* Validation Error Banner */}
      {errorMessages.length > 0 && (
        <div className="sticky top-0 z-10 px-6 py-3 bg-red-50 border-b border-red-200">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-red-800 mb-1.5">
                {errorMessages.length} error
                {errorMessages.length !== 1 ? "s" : ""} found:
              </h4>
              <div className="max-h-24 overflow-y-auto pr-2">
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {errorMessages.map((error, index) => (
                    <li key={index} className="leading-relaxed">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="space-y-4 p-6">
        {renderGrid(fields, {
          formData: form,
          onChange: handleChange,
          showValidation: true,
          searchParams: {
            initialValues: {
              projectId: form?.projectId || form?.project,
              projectObjectiveId:
                form?.projectObjectiveId || form?.projectObjective,
            },
          },
        })}
      </div>
    </Modal>
  );
}
